import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { Avatar } from "./common";
import { Plus, X, ChevronLeft, ChevronRight, Radio, Repeat2, Music2, Play } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// Swipeable carousel for a re-shared multi-image post inside a Story card.
function SharedCarousel({ media }) {
  const [idx, setIdx] = useState(0);
  const ref = useRef();
  const onScroll = () => { const el = ref.current; if (el && el.clientWidth) setIdx(Math.round(el.scrollLeft / el.clientWidth)); };
  const go = (dir) => { const el = ref.current; if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" }); };
  return (
    <div className="relative" data-testid="story-shared-carousel">
      <div ref={ref} onScroll={onScroll} className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar">
        {media.map((m, i) => (
          <img key={i} src={mediaUrl(m.url)} alt="" className="w-full shrink-0 snap-center max-h-[52vh] object-contain bg-black" />
        ))}
      </div>
      <span className="absolute top-2 right-2 bg-black/65 rounded-full px-2.5 py-0.5 text-xs font-medium">{idx + 1}/{media.length}</span>
      {idx > 0 && <button onClick={() => go(-1)} data-testid="story-shared-prev" className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 items-center justify-center"><ChevronLeft size={18} /></button>}
      {idx < media.length - 1 && <button onClick={() => go(1)} data-testid="story-shared-next" className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 items-center justify-center"><ChevronRight size={18} /></button>}
      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
        {media.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-white" : "w-1.5 bg-white/45"}`} />)}
      </div>
    </div>
  );
}

export function StoryBar({ groups, onAdd, onOpen, lives = [] }) {
  const { user } = useAuth();
  return (
    <div className="flex gap-3 overflow-x-auto hide-scroll py-1" data-testid="story-bar">
      <button onClick={onAdd} className="flex flex-col items-center gap-1.5 shrink-0 w-[72px]" data-testid="add-story-btn">
        <div className="relative w-16 h-16 rounded-full surface-2 border border-white/10 flex items-center justify-center">
          <Avatar src={user?.avatar_url} name={user?.display_name} size={60} />
          <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full btn-blue flex items-center justify-center border-2 border-[#030303]"><Plus size={14} /></span>
        </div>
        <span className="text-[11px] text-muted-foreground">Your Story</span>
      </button>
      {lives.map((ls) => (
        <Link key={ls.id} to={`/live/${ls.id}`} className="flex flex-col items-center gap-1.5 shrink-0 w-[72px]" data-testid={`story-live-${ls.id}`}>
          <div className="relative w-16 h-16 rounded-full p-[2px] bg-destructive">
            <div className="w-full h-full rounded-full border-2 border-[#030303] overflow-hidden"><Avatar src={ls.creator?.avatar_url} name={ls.creator?.display_name} size={60} /></div>
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] font-bold bg-destructive text-white px-1.5 py-[1px] rounded-full flex items-center gap-0.5 border border-[#030303]"><span className="w-1 h-1 rounded-full bg-white live-dot" />LIVE</span>
          </div>
          <span className="text-[11px] truncate w-full text-center">{ls.creator?.display_name?.split(" ")[0] || "Live"}</span>
        </Link>
      ))}
      {groups.map((g, i) => (
        <button key={g.author.id} onClick={() => onOpen(i)} className="flex flex-col items-center gap-1.5 shrink-0 w-[72px]" data-testid={`story-${g.author.username}`}>
          <div className="w-16 h-16 rounded-full p-[2px]" style={{ background: "conic-gradient(from 180deg, var(--gold), var(--eblue), var(--gold))" }}>
            <div className="w-full h-full rounded-full border-2 border-[#030303] overflow-hidden"><Avatar src={g.author.avatar_url} name={g.author.display_name} size={60} /></div>
          </div>
          <span className="text-[11px] truncate w-full text-center">{g.author.display_name?.split(" ")[0]}</span>
        </button>
      ))}
    </div>
  );
}

export function StoryViewer({ groups, startIndex, onClose }) {
  const [gi, setGi] = useState(startIndex);
  const [si, setSi] = useState(0);
  const timer = useRef();
  const group = groups[gi];
  const story = group?.stories?.[si];

  useEffect(() => {
    if (!story) return;
    api.post(`/stories/${story.id}/view`).catch(() => {});
    clearTimeout(timer.current);
    const n = story.shared_ref?.media?.length > 1 ? story.shared_ref.media.length : 1;
    timer.current = setTimeout(nextStory, 5000 * n);
    return () => clearTimeout(timer.current);
  }, [gi, si]);

  const nextStory = () => {
    if (si + 1 < group.stories.length) setSi(si + 1);
    else if (gi + 1 < groups.length) { setGi(gi + 1); setSi(0); }
    else onClose();
  };
  const prevStory = () => {
    if (si > 0) setSi(si - 1);
    else if (gi > 0) { setGi(gi - 1); setSi(0); }
  };

  if (!story) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center" data-testid="story-viewer">
      <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 text-white/80 hover:text-white"><X size={26} /></button>
      <button onClick={prevStory} className="absolute left-2 md:left-8 z-10 p-2 text-white/60 hover:text-white"><ChevronLeft size={30} /></button>
      <button onClick={nextStory} className="absolute right-2 md:right-8 z-10 p-2 text-white/60 hover:text-white"><ChevronRight size={30} /></button>
      <div className="relative w-full max-w-[420px] aspect-[9/16] bg-[#0b1021] rounded-xl overflow-hidden">
        <div className="absolute top-2 inset-x-2 z-10 flex gap-1">
          {group.stories.map((_, i) => (
            <div key={i} className="h-1 flex-1 rounded-full bg-white/25 overflow-hidden">
              <div className="h-full bg-white" style={{ width: i < si ? "100%" : i === si ? "100%" : "0%", transition: i === si ? "width 5s linear" : "none" }} />
            </div>
          ))}
        </div>
        <div className="absolute top-5 inset-x-3 z-10 flex items-center gap-2 mt-2">
          <Avatar src={group.author.avatar_url} name={group.author.display_name} size={32} />
          <Link to={`/${group.author.username}`} onClick={onClose} className="text-sm font-semibold text-white">{group.author.display_name}</Link>
        </div>
        {story.shared_ref ? (() => {
          const sr = story.shared_ref;
          const isTrack = sr.kind === "track";
          const isVideo = sr.kind === "video";
          const isMedia = isTrack || isVideo;
          const kindLabel = sr.kind === "reel" ? "VReel" : isTrack ? "VStar Music" : isVideo ? "VStar TV" : "VFeed";
          const KindIcon = isTrack ? Music2 : isVideo ? Play : Repeat2;
          const mediaInner = (
            <div className={`relative bg-black ${isTrack ? "aspect-square" : "aspect-video"}`}>
              {sr.cover
                ? <img src={mediaUrl(sr.cover)} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-white/30"><KindIcon size={48} /></div>}
              <span className="absolute inset-0 flex items-center justify-center"><span className="w-14 h-14 rounded-full btn-gold flex items-center justify-center shadow-lg"><Play size={24} /></span></span>
            </div>
          );
          return (
            <div className="w-full h-full flex flex-col items-center justify-center p-5 pt-16">
              <div className="w-full rounded-2xl overflow-hidden border border-gold/30 bg-gradient-to-b from-[#161b2e] to-[#0b0f1c] shadow-[0_0_40px_rgba(242,199,74,0.18)]">
                <Link to={`/${sr.author?.username}`} onClick={onClose} className="flex items-center gap-2 p-3">
                  <div className="rounded-full p-[2px]" style={{ background: "conic-gradient(from 180deg, var(--gold), var(--eblue), var(--gold))" }}>
                    <Avatar src={sr.author?.avatar_url} name={sr.author?.display_name} size={34} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-white truncate">{sr.author?.display_name}</div>
                    <div className="text-[11px] text-muted-foreground">@{sr.author?.username}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wide font-bold text-gold bg-gold/10 border border-gold/30 rounded-full px-2 py-0.5">{kindLabel}</span>
                </Link>
                {isMedia
                  ? (isVideo ? <Link to={`/video/${sr.id}`} onClick={onClose} className="block">{mediaInner}</Link> : mediaInner)
                  : (sr.media?.length > 1
                    ? <SharedCarousel media={sr.media} />
                    : sr.media_url && (sr.media_type === "video"
                      ? <video src={mediaUrl(sr.media_url)} className="w-full max-h-[48vh] object-contain bg-black" autoPlay loop muted playsInline />
                      : <img src={mediaUrl(sr.media_url)} alt="" className="w-full max-h-[48vh] object-contain bg-black" />))}
                {isMedia && sr.title && (
                  <div className="px-3 pt-3"><div className="text-sm font-semibold text-white flex items-center gap-1.5"><KindIcon size={14} className="text-eblue" />{sr.title}</div></div>
                )}
                {sr.text && <p className="text-sm text-white/90 p-3 whitespace-pre-wrap line-clamp-3">{sr.text}</p>}
                <div className="flex items-center justify-between px-3 py-2 border-t border-white/10 bg-black/25">
                  <span className="font-display font-bold text-xs tracking-wide"><span className="text-white">VIRTU</span><span className="text-gold">STARS</span></span>
                  <span className="text-[10px] text-white/55 flex items-center gap-1"><Repeat2 size={12} /> Shared by {group.author.display_name}</span>
                </div>
              </div>
            </div>
          );
        })() : story.media_url ? <img src={mediaUrl(story.media_url)} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center p-8 text-center font-display text-2xl">{story.text}</div>}
        {!story.shared_ref && story.media_url && story.text && <div className="absolute bottom-10 inset-x-0 text-center px-6 font-display text-xl drop-shadow-lg">{story.text}</div>}
      </div>
    </div>
  );
}
